/**
 * The village's starter loop rules (ported from the wuxia sample's gameplay.ts; docs/research/STARTER_COMBAT.md):
 * - the flow: intro → the boar quest (`STARTER_QUEST`) → the countdown → the world boss → victory (`StarterFlow`);
 * - no character levels (GDD pillar 3): kills pay แต้มฝึกปรือ and ตำลึง; the most-used skill ranks up while the points
 *   cover `skillCost` (`villageRankUps`), and the ระดับฝีมือ title follows the points spent (`villageRankTitle`);
 * - blows through the shared triangle (`resolveHit`), regen from `QI` and the derived stats, aim and area tests;
 * - the boss's chest: the contribution tier (`tierOf`), `WB_CHEST`, rares × `rareMul`, then the `worldboss` row.
 * Pure: randomness through the injected `Rng`, time through dt.
 */
import { resolveHit, type HitResult } from '../combat';
import { MAX_SKILL_LEVEL, rankName, rankThreshold, skillCost } from '../cultivation';
import { STARTER_DROPS } from '../data/drops';
import type { MonsterDef } from '../data/monsters';
import { QI } from '../data/qi';
import { STARTER_QUEST } from '../data/quests';
import type { SkillDef, SkillKind } from '../data/skills';
import { STARTER } from '../data/starter';
import { WB_CHEST, tierOf, type WbTier, type WorldBossDef } from '../data/worldBosses';
import type { Rng } from '../rng';
import { BASE_ATTRIBUTES, type DerivedStats } from '../stats';
import { rollVillageDrops, type VillageLootRoll } from './loot';

export type StarterPhase = 'intro' | 'boars' | 'bossSoon' | 'boss' | 'victory';

/** The quest's kill count. */
export const STARTER_KILLS = STARTER_QUEST.objectives[0]!.count;

/** The starter flow: the phase, its clock and the quest's kills. */
export class StarterFlow {
  phase: StarterPhase = 'intro';
  /** Seconds in the phase. */
  t = 0;
  kills = 0;

  /** Advances the clock. `countdown`: whole seconds left to show (bossSoon, after the banner); `boss`: it lands now. */
  step(dt: number): { countdown: number; boss: boolean } {
    this.t += dt;
    if (this.phase === 'intro' && this.t > STARTER.intro) this.phase = 'boars';
    let countdown = 0;
    let boss = false;
    if (this.phase === 'bossSoon') {
      const left = Math.ceil(STARTER.bossCountdown + STARTER.bossBanner - this.t);
      if (this.t > STARTER.bossBanner && left > 0) countdown = left;
      if (left <= 0) {
        this.phase = 'boss';
        boss = true;
      }
    }
    return { countdown, boss };
  }

  /** A quest monster died. True: the quest is done now (the countdown starts). */
  kill(): boolean {
    if (this.phase !== 'boars') return false;
    this.kills++;
    if (this.kills < STARTER_KILLS) return false;
    this.phase = 'bossSoon';
    this.t = 0;
    return true;
  }

  victory(): void {
    this.phase = 'victory';
  }

  reset(): void {
    this.phase = 'intro';
    this.t = 0;
    this.kills = 0;
  }
}

/** The ระดับฝีมือ title for the points spent. */
export function villageRankTitle(spent: number): string {
  let k = 0;
  while (rankThreshold(k + 1) <= spent) k++;
  return rankName(k);
}

/** The heroine's progress: points banked and spent, each skill's rank and uses (bar order). */
export interface VillageProgress {
  cultivation: number;
  spent: number;
  gold: number;
  ranks: Map<string, number>;
  uses: Map<string, number>;
}

/** Pays points and gold, then ranks up the most-used skill while the points cover it. Returns each rank-up (id, new rank, points spent by then). */
export function villageGain(p: VillageProgress, cultivation: number, gold: number): { id: string; rank: number; spent: number }[] {
  p.cultivation += cultivation;
  p.gold += gold;
  const ups: { id: string; rank: number; spent: number }[] = [];
  for (;;) {
    const top = [...p.uses.entries()].sort((a, b) => b[1] - a[1])[0]![0];
    const r = p.ranks.get(top)!;
    const cost = skillCost(r);
    if (r >= MAX_SKILL_LEVEL || p.cultivation < cost) break;
    p.cultivation -= cost;
    p.spent += cost;
    p.ranks.set(top, r + 1);
    ups.push({ id: top, rank: r + 1, spent: p.spent });
  }
  return ups;
}

/** A kill's pay: the row's points and a gold roll in its range. */
export function villageKillReward(def: MonsterDef, rng: Rng): { cultivation: number; gold: number } {
  const [lo, hi] = def.reward.gold;
  return { cultivation: def.reward.cultivation, gold: lo + Math.floor(rng() * (hi - lo + 1)) };
}

/** A kill's drops: the monster's own loot, then its village drop row. */
export function villageKillDrops(def: MonsterDef, rng: Rng): VillageLootRoll[] {
  return rollVillageDrops([...(def.loot ?? []), ...(STARTER_DROPS[def.id] ?? [])], def.level, rng);
}

/** The boss's chest for one fresh player (rank 0 of 1): tier, the chest row, the rare rolls × `rareMul`, then `worldboss`. */
export function villageBossChest(boss: WorldBossDef, rng: Rng): { tier: WbTier; chest: (typeof WB_CHEST)[WbTier]; drops: VillageLootRoll[] } {
  const tier = tierOf(0, 1);
  const chest = WB_CHEST[tier];
  const drops = rollVillageDrops([...boss.rare.map((r) => ({ item: r.item, chance: r.chance * chest.rareMul })), ...(STARTER_DROPS.worldboss ?? [])], boss.level, rng);
  return { tier, chest, drops };
}

/** The heroine's blow on a target (the triangle; `casting` is the target's wind-up kind). */
export function villageHeroHit(P: DerivedStats, power: number, kind: SkillKind, def: number, casting: SkillKind | null, rng: Rng): HitResult {
  return resolveHit({ atk: P.atk, power, kind, critChance: P.crit, hit: P.hit }, { blocking: false, stamina: 100, invulnerable: false, castingKind: casting, def }, rng);
}

/** A monster's move on the heroine (`invulnerable`: the dash's window; `casting`: her own wind-up kind). */
export function villageMobHit(m: MonsterDef, move: SkillDef, P: DerivedStats, invulnerable: boolean, casting: SkillKind | null, rng: Rng): HitResult {
  return resolveHit(
    { atk: m.atk, power: move.power, kind: move.kind, stun: move.stun, critChance: STARTER.mobCrit },
    { blocking: false, stamina: 100, invulnerable, castingKind: casting, def: P.def, dodge: P.dodge },
    rng,
  );
}

/** Regen over dt: qi from `QI` (+ the calm rate), health from the derived regen (+ `STARTER.hpCalmPerSec` calm). */
export function villageRegen(v: { hp: number; qi: number }, P: DerivedStats, calm: boolean, dt: number): void {
  v.qi = Math.min(P.maxQi, v.qi + (QI.regenBase + BASE_ATTRIBUTES.spi * QI.regenPerSpi + (calm ? P.maxQi * QI.outOfCombatPerSec : 0)) * dt);
  v.hp = Math.min(P.maxHp, v.hp + (P.hpRegen + (calm ? P.maxHp * STARTER.hpCalmPerSec : 0)) * dt);
}

/** A target circle (x, z, radius). */
export interface VillageTarget {
  x: number;
  z: number;
  r: number;
}

/** The nearest target whose edge is within `reach` of (px, pz), or null. */
export function villageNearest<T extends VillageTarget>(targets: readonly T[], px: number, pz: number, reach: number): T | null {
  let best: T | null = null;
  let bd = reach;
  for (const t of targets) {
    const d = Math.hypot(t.x - px, t.z - pz) - t.r;
    if (d < bd) {
      bd = d;
      best = t;
    }
  }
  return best;
}

/** Is a target in the cone (half angle; ≥ π: a circle) of `reach` round (x, z) facing `yaw`? */
export function villageInArea(t: VillageTarget, x: number, z: number, yaw: number, reach: number, halfAngle: number): boolean {
  const dx = t.x - x;
  const dz = t.z - z;
  const d = Math.hypot(dx, dz);
  if (d > reach + t.r) return false;
  if (halfAngle < Math.PI && d > t.r && (dx * Math.sin(yaw) + dz * Math.cos(yaw)) / d < Math.cos(halfAngle)) return false;
  return true;
}

/** A boss move's damage on the heroine: its `share` of her health × the multiplier. */
export const villageBossDamage = (share: number, maxHp: number, mul: number): number => Math.round(share * maxHp * mul);
